import { BE_BASE_URL } from "@lib/constants/AppConst";
import { ApiParams } from "@lib/types/Api";
import { IRouteConfig } from "@lib/types/Routes";
import { FilterValue } from "antd/es/table/interface";
import { TablePaginationConfig } from "antd/lib";
import axios from "axios";
import dayjs from "dayjs";
import isEqual from "lodash/isEqual";
import { Params } from "react-router-dom";

export const getAssetsUrl = (fileUrl: string) => {
  return `/assets/images/${fileUrl}`;
};

export const cleanDomain = (url: string): string => {
  const remove = ["https://www.", "http://www.", "https://", "http://", "www."];

  for (let index = 0; index < remove.length; index++) {
    if (url.startsWith(remove[index])) {
      url.replace(url, remove[index]);
    }
  }

  return url;
};

type TableChange = {
  pagination: TablePaginationConfig;
  filters: Record<string, FilterValue>;
  sorter: any;
  params: ApiParams;
  setParams: (params: Partial<ApiParams>) => void;
};

export const handleTableChange = (props: TableChange) => {
  const { filters, sorter, params, setParams } = props;
  // console.log("🚀 ~ handleTableChange ~ filters:", filters);
  const filter = Object.entries(filters as Record<string, string | string[]>)
    .filter(([, value]) => value)
    .map(([key, value]) => {
      if (Array.isArray(value) && value.length > 1) {
        const [start, end] = value;
        const isDateRange = dayjs(start).isValid() && dayjs(end).isValid();
        const isNumberRange = !isNaN(Number(start)) && !isNaN(Number(end));

        if (isDateRange) {
          return `${key}||$between||${start},${end}`;
        } else if (isNumberRange) {
          return `${key}||$between||${start},${end}`;
        } else {
          return `${key}||$in||${value.join(",")}`;
        }
      } else if (typeof value === "string" && value.includes("%")) {
        return `${key}||$cont||${value.replace(/%/g, "")}`;
      } else {
        return `${key}||$eq||${value}`;
      }
    });

  let sortParam = sorter?.order || params?.sort;
  if (!Array.isArray(sorter) && sorter?.field && sorter?.order) {
    const order = sorter.order === "ascend" ? "ASC" : "DESC";
    sortParam = `${sorter.field},${order}`;
  }

  const newParams = {
    // page: pagination?.current || 1,
    // limit: pagination?.pageSize || 10,
    filter: filter,
    sort: sortParam,
  };

  if (!isEqual(newParams, params)) {
    setParams(newParams);
  }
};

export function parseFiltersFromUrl(
  url: string,
): { key: string; operator: string; value: string }[] {
  const urlObj = new URL(url, BE_BASE_URL);
  const searchParams = new URLSearchParams(urlObj.search);

  const filters = [];

  searchParams.forEach((value, key) => {
    if (key === "filter") {
      const filterParts = value.split("||");
      if (filterParts.length === 3) {
        filters.push({
          key: filterParts[0],
          operator: filterParts[1],
          value: decodeURIComponent(filterParts[2]),
        });
      }
    }
  });

  return filters;
}

export const parseGetParamsFromUrl = (url: string): Record<string, string> => {
  const urlObj = new URL(url, BE_BASE_URL);
  const searchParams = new URLSearchParams(urlObj.search);
  return Object.fromEntries(searchParams.entries());
};

export const isItRealUrl = (url: string): boolean => {
  const protocol = url.split("://")[0] || "";
  const domain = url.split("://")[1]?.split("/")[0] || "";
  if (!protocol || !domain || domain.split(".").length < 2) return false;

  return true;
};

export const healthCheckCrmDomain = async (
  input: string,
  suffix: string = "/api/v1/healthcheck",
): Promise<{ status: boolean; message: string }> => {
  const protocol = input.split("://")[0] || "";
  const domain = input.split("://")[1]?.split("/")[0] || "";
  const path = input.split("://")[1]?.split("/")[1] || "";

  if (!protocol || !domain || path)
    return { status: false, message: "error.domain.invalid" };
  if (!domain.includes("."))
    return { status: false, message: "error.domain.tld" };
  if (domain.length < 5)
    return { status: false, message: "error.domain.length" };
  if (input.endsWith("/"))
    return { status: false, message: "error.domain.slash" };

  try {
    const result = await axios.get<{ status: string }>(
      `${protocol}://${domain}${suffix}`,
    );
    return {
      status: Boolean(result.data?.status),
      message: "error.domain.connection",
    };
  } catch {
    return { status: false, message: "error.domain.connection" };
  }
};

export const findSelectedKeys = ({
  routes,
  path,
  params,
}: {
  routes: IRouteConfig[];
  path: string;
  params: Readonly<Params<string>>;
}): { found: string[]; notFound: string[]; path: string } => {
  path = Object.entries(params).reduce((acc, [key, value]) => {
    return acc.replace(value, `:${key}`);
  }, path);

  const pathSegments = path.split("/").filter((segment) => segment.length > 0);

  for (let i = pathSegments.length; i > 0; i--) {
    const currentPath = "/" + pathSegments.slice(0, i).join("/");
    const remainingSegments = pathSegments.slice(i);

    for (const route of routes) {
      if (route.url === currentPath) {
        return {
          found: [route.id],
          notFound: remainingSegments,
          path,
        };
      }
      if (route.children) {
        const childResult = findSelectedKeys({
          routes: route.children,
          path: currentPath,
          params,
        });
        if (childResult.found.length > 0) {
          return {
            found: [route.id, ...childResult.found],
            notFound: [...childResult.notFound, ...remainingSegments],
            path,
          };
        }
      }
    }
  }

  return {
    found: [],
    notFound: pathSegments,
    path,
  };
};

/**
 * Gets the breadcrumb items for a given route id.
 * @param id The id of the route to get breadcrumb items for.
 * @returns The breadcrumb items for the given route id.
 */
export const getBreadcrumbItems = (
  id: string,
  routesConfig: IRouteConfig[],
) => {
  const mainLinks = routesConfig.find((route) => route.id === id);
  const breadcrumbItems = mainLinks?.children?.map((child) => {
    return {
      title: child.messageId,
      url: child.url,
      key: child.id,
      icon: child.icon,
    };
  });

  return breadcrumbItems || [];
};

/**
 * Finds a route by its id.
 * @param id The id of the route to find.
 * @param routes The routes to search through.
 * @returns The route if found, otherwise undefined.
 */
export function findRouteById(
  id: string,
  routes: IRouteConfig[],
): IRouteConfig | undefined {
  for (const route of routes) {
    if (route.id === id) {
      return route;
    }
    if (route.children) {
      const found = findRouteById(id, route.children);
      if (found) {
        return found;
      }
    }
  }
  return undefined;
}

export const filterBottomNavRoutes = (
  routes: IRouteConfig[],
): IRouteConfig[] => {
  const filteredRoutes: IRouteConfig[] = [];

  const filterRoutes = (routeList: IRouteConfig[]) => {
    for (const route of routeList) {
      if (route.showInBottomNav === true) {
        filteredRoutes.push(route);
      }

      if (route.children && route.children.length > 0) {
        filterRoutes(route.children);
      }
    }
  };

  filterRoutes(routes);
  return filteredRoutes;
};

/**
 * Extracts the selected keys from a pathname by removing the leading slash
 * @param pathname The current pathname from useLocation
 * @returns The selected keys string without leading slash
 */
export const getSelectedKeysFromPathname = (pathname: string): string => {
  return pathname.startsWith("/") ? pathname.substring(1) : pathname;
};

/**
 * Gets the default open key from a pathname (first segment)
 * @param pathname The current pathname from useLocation
 * @returns The first segment of the pathname
 */
export const getDefaultOpenKeyFromPathname = (pathname: string): string => {
  const selectedKeys = getSelectedKeysFromPathname(pathname);
  return selectedKeys?.split("/")[0] || "";
};

/**
 * Generates an array of selected paths from a pathname
 * @param pathname The current pathname from useLocation
 * @returns Array of path segments for menu selection
 */
export const getSelectedPathsFromPathname = (pathname: string): string[] => {
  const selectedKeys = getSelectedKeysFromPathname(pathname);

  if (!selectedKeys) return [];

  const parts = selectedKeys.split("/").filter(Boolean);
  const paths: string[] = [];
  let currentPath = "";

  for (const part of parts) {
    currentPath += "/" + part;
    paths.push(currentPath);
  }

  return paths;
};

/**
 * Cleans a pathname by removing query parameters and hash fragments
 * @param pathname The current pathname from useLocation
 * @returns Clean pathname without query params and hash
 */
export const getCleanPathname = (pathname: string): string => {
  return pathname?.split("?")[0]?.split("#")[0] || "";
};

/**
 * Gets the selected key for menu navigation from a pathname
 * @param pathname The current pathname from useLocation
 * @returns Clean pathname suitable for menu selection
 */
export const getSelectedKeyFromPathname = (pathname: string): string => {
  return getCleanPathname(pathname);
};

/**
 * Comprehensive URL path analysis for navigation components
 * @param pathname The current pathname from useLocation
 * @returns Object containing all path-related data for navigation
 */
export const analyzePathnameForNavigation = (pathname: string) => {
  const cleanPathname = getCleanPathname(pathname);
  const selectedKeys = getSelectedKeysFromPathname(pathname);
  const defaultOpenKey = getDefaultOpenKeyFromPathname(pathname);
  const selectedPaths = getSelectedPathsFromPathname(pathname);
  const selectedKey = getSelectedKeyFromPathname(pathname);

  return {
    cleanPathname,
    selectedKeys,
    defaultOpenKey,
    selectedPaths,
    selectedKey,
  };
};

export const urlReplaceParams = (
  url: string,
  params: Record<string, string>,
) => {
  return Object.entries(params).reduce((acc, [key, value]) => {
    return acc.replace(`:${key}`, value);
  }, url);
};
